import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type ValueToLabelCache = ReturnType<typeof useValueToLabelCache>;

type Options = {
  base?: Record<string, string>;
};

/**
 * 値からラベルへのキャッシュを管理するフック。コンボボックスやフィルタチップで利用する。
 * @returns 値からラベルへのキャッシュを管理するオブジェクト
 */
export default function useValueToLabelCache(options?: Options) {
  const [valueToLabel, setValueToLabel] = useState(options?.base ?? {});
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>();

  // baseの前回値を保持して、変更時のみ更新する
  const prevBaseRef = useRef(options?.base);

  const has = useCallback(
    (value: string) => valueToLabel[value] !== undefined,
    [valueToLabel],
  );

  const get = useCallback(
    (value: string) => valueToLabel[value],
    [valueToLabel],
  );

  const set = useCallback((value: string, label: string) => {
    setValueToLabel((prev) => {
      if (prev[value] === label) return prev;
      return { ...prev, [value]: label };
    });
    setLastUpdatedAt(Date.now());
  }, []);

  const setBulk = useCallback((values: Record<string, string>) => {
    setValueToLabel((prev) => {
      const next = { ...prev, ...values };
      if (isEqual(prev, next)) return prev;
      return next;
    });
    setLastUpdatedAt(Date.now());
  }, []);

  const all = useCallback(() => valueToLabel, [valueToLabel]);

  const clear = useCallback(() => {
    setValueToLabel({});
    setLastUpdatedAt(Date.now());
  }, []);

  const getUpdatedAt = useCallback(() => lastUpdatedAt, [lastUpdatedAt]);

  // baseが変更された時のみマージする
  useEffect(() => {
    if (options?.base && !isEqual(options.base, prevBaseRef.current)) {
      prevBaseRef.current = options.base;
      setBulk(options.base);
    }
  }, [options?.base, setBulk]);

  return useMemo(
    () => ({ has, get, set, setBulk, all, clear, getUpdatedAt }),
    [has, get, set, setBulk, all, clear, getUpdatedAt],
  );
}
